/**
 * Drafts — per-channel composer text persisted to localStorage.
 *
 * Telegram-grade polish: leave a half-typed message in #general, switch
 * to a DM to check something, come back — your text is still there. Same
 * if you accidentally close the tab.
 *
 * Why client-only (and not synced via the server):
 *   - A draft is private until sent. Round-tripping every keystroke is
 *     bandwidth + privacy noise.
 *   - Cross-device sync is a maybe-later feature; for v1 per-device is
 *     consistent with the rest of the local UX state (saved messages,
 *     theme, voice prefs).
 *
 * Storage shape — single JSON blob under `bunker:drafts` keyed by
 * channelId. We compact-write the whole map on every change; the data
 * is tiny (typically <2 KB across all channels) so the I/O cost is
 * irrelevant compared to the simplicity of one storage key.
 *
 * Pub/sub — `drafts:changed` window event for in-tab listeners, plus
 * the native `storage` event covers other tabs. DM rows / channel list
 * use this to render the "Черновик: …" indicator.
 *
 * Writes are debounced (250ms) so the textarea doesn't hammer
 * localStorage on every keypress. The hook flushes pending writes on
 * channel switch / unmount so nothing gets lost.
 */

const STORAGE_KEY = 'bunker:drafts';
const CHANGE_EVENT = 'drafts:changed';
const FLUSH_DELAY_MS = 250;

type DraftMap = Record<string, string>;

function readAll(): DraftMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Defensive: corrupted entry or wrong shape → reset gracefully.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    // Filter to string values only.
    const out: DraftMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string' && v.length > 0) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writeAll(next: DraftMap) {
  if (typeof window === 'undefined') return;
  try {
    if (Object.keys(next).length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    // Quota exceeded or storage disabled — silent no-op. Drafts are
    // best-effort UX; we don't want to throw inside an input onChange.
  }
}

/** Read the persisted draft for a channel. Empty string when none. */
export function getDraft(channelId: string): string {
  return readAll()[channelId] ?? '';
}

/** Snapshot of all channelIds that currently have a non-empty draft. */
export function listDraftChannels(): string[] {
  return Object.keys(readAll());
}

// In-memory pending writes, debounced. Keyed by channelId so concurrent
// edits to different channels don't trample each other.
const pending: Map<string, { value: string; timer: ReturnType<typeof setTimeout> }> =
  new Map();

function commit(channelId: string, value: string) {
  pending.delete(channelId);
  const all = readAll();
  if (value.length === 0) {
    if (!(channelId in all)) return;
    delete all[channelId];
  } else {
    if (all[channelId] === value) return;
    all[channelId] = value;
  }
  writeAll(all);
}

/**
 * Save (or clear) the draft for one channel. Debounced so the textarea
 * onChange handler can call us on every keystroke without DOS'ing the
 * storage layer. Pass an empty string to drop the draft.
 */
export function setDraft(channelId: string, value: string): void {
  const existing = pending.get(channelId);
  if (existing) clearTimeout(existing.timer);
  const timer = setTimeout(() => commit(channelId, value), FLUSH_DELAY_MS);
  pending.set(channelId, { value, timer });
}

/**
 * Force-flush any pending debounced write for a channel. Call this on
 * Composer unmount / channel switch / send so the latest state lands
 * before the component goes away.
 */
export function flushDraft(channelId: string): void {
  const existing = pending.get(channelId);
  if (!existing) return;
  clearTimeout(existing.timer);
  commit(channelId, existing.value);
}

/** Drop the draft immediately (no debounce). Used after a successful send. */
export function clearDraft(channelId: string): void {
  const existing = pending.get(channelId);
  if (existing) clearTimeout(existing.timer);
  pending.delete(channelId);
  const all = readAll();
  if (!(channelId in all)) return;
  delete all[channelId];
  writeAll(all);
}

/**
 * Subscribe to draft mutations. Returns an unsubscribe function. Fires
 * on both our own dispatched events and the native storage event so a
 * change in another tab also propagates.
 */
export function subscribeDrafts(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const onLocal = () => listener();
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) listener();
  };
  window.addEventListener(CHANGE_EVENT, onLocal);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onLocal);
    window.removeEventListener('storage', onStorage);
  };
}
